import React, { useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toastWarning } from 'utils/toast';
import { encryptURL } from 'utils/functions';
import { OrçamentosContext } from 'contexts/OrçamentosContext';
import { DocumentosContext } from 'contexts/DocumentosContext';
import { RelatoriosContext } from 'contexts/RelatoriosContext';
import { DropsContext } from 'contexts/DropsContext';
import { GridContext } from 'contexts/GridContext';
import { AuthContext } from 'contexts/AuthContext';
import { useDialog, useModal } from 'components/Modals';
import ResumoModal from 'components/Modals/ResumoModal';
import ContatoModal from 'components/Modals/ContatoModal';
import Container from 'components/Container';
import Header from 'components/Header';
import Card from 'components/Card';
import DataView, { useDataViewFilters } from 'components/DataView';
import getOrçamentosFilterFields from './filterFields';
import columnOverrides from './columnOverrides';

const Orçamentos = () => {
  const rotina = 'ServicosOrçamentos';
  const titulo = 'Orçamentos';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openModal, closeModal } = useModal();
  const { openDialog, closeDialog } = useDialog();
  const { user } = useContext(AuthContext);
  const { drops } = useContext(DropsContext);
  const { postDocumento, postLoading: docLoad } = useContext(DocumentosContext);
  const { exportPdf, exportXls } = useContext(GridContext);
  const { getURLRelatorio } = useContext(RelatoriosContext);
  const {
    orçamentos,
    getLoading,
    getOrçamentos,
    postStatus,
    postServico,
    postLoading,
  } = useContext(OrçamentosContext);
  const filterFields = useMemo(
    () => getOrçamentosFilterFields(drops),
    [drops]
  );
  const loading = getLoading || postLoading || docLoad;

  const { applyFilter, filterForm } = useDataViewFilters({
    fields: filterFields,
    searchParams,
    setSearchParams,
    onApply: (props) => loadGrid(props),
  });
  const { getValues } = filterForm;

  const loadGrid = (props = {}) => {
    const filter = props?.filter || getValues();
    closeModal();
    closeDialog();
    getOrçamentos({ ...props, rotina, filter });
  };

  const changeStatus = ({ documento_id, status_id, motivo }) => {
    const data = { Registros: [{ documento_id, status_id, motivo }] };
    postDocumento({ data, cb: loadGrid });
  };

  const toolbarActions = [
    {
      name: 'Adicionar Orçamento',
      icon: 'add',
      placement: 'primary',
      show: !Boolean(user?.permissoes?.block_criar_orcamento),
      action: () => navigate('/app/Orcamentos/Gerar'),
    },
    {
      name: 'Exportar PDF',
      icon: 'file_download',
      action: () =>
        exportPdf({ rotina, filter: getValues(), order: orçamentos?.order }),
    },
    {
      name: 'Exportar XLS',
      icon: 'file_download',
      action: () =>
        exportXls({ rotina, filter: getValues(), order: orçamentos.order }),
    },
    {
      name: 'Resumo',
      icon: 'summarize',
      action: () =>
        openModal(
          <ResumoModal
            colunas={orçamentos?.colunas}
            onSubmit={(group) =>
              navigate('/app/Resumo', {
                state: { rotina, group, filter: getValues() },
              })
            }
          />
        ),
    },
    {
      name: 'Gerar Ordem de Serviço',
      icon: 'post_add',
      show: !Boolean(user?.permissoes?.block_gerar_servico),
      action: (selected) => {
        if (!Boolean(selected?.length)) {
          return toastWarning('Selecione ao menos um item');
        }
        if (selected?.some((s) => s?.status !== 'PENDENTE')) {
          return toastWarning('Selecione apenas orçamentos pendentes');
        }
        const data = { documentos: selected?.map((s) => s?.id) };
        postServico({ data, cb: loadGrid });
      },
    },
  ];

  const rowActions = [
    {
      name: 'Em execução',
      icon: 'schedule',
      color: 'primary.main',
      show: ({ row }) => Boolean(row?.executantes),
    },
    {
      name: 'Detalhes',
      icon: 'visibility',
      action: ({ row }) => navigate(`/app/Documento/${row?.id}`),
    },
    {
      name: 'Direcionamento Técnico',
      icon: 'build',
      menu: true,
      show: ({ row }) =>
        !Boolean(user?.permissoes?.block_direcionamento_tecnico) &&
        user?.nivel !== 'TÉCNICO' &&
        row?.status === 'PENDENTE',
      action: ({ row }) =>
        navigate(`/app/Servicos/Direcionamento/${encryptURL(row)}`),
    },
    {
      name: 'Encaminhar Orçamento',
      icon: 'request_quote',
      menu: true,
      show: ({ row }) =>
        !Boolean(user?.permissoes?.block_encaminhar_orcamento) &&
        !['TÉCNICO']?.includes(user?.nivel) &&
        [1, 17, 25, 45]?.includes(row?.status_id),
      action: ({ row }) => {
        if (row?.vldocto <= 0) {
          return toastWarning(
            'Não é possível encaminhar um orçamento sem valor'
          );
        }

        getURLRelatorio({
          data: { codigo: 'OSORC00001', documento_id: row?.id },
          documento: row,
          cb: loadGrid,
        });
      },
    },
    {
      name: 'Finalizar Orçamento',
      icon: 'done',
      menu: true,
      show: ({ row }) =>
        !Boolean(user?.permissoes?.block_finalizar_orcamento) &&
        !['ATENDENTE']?.includes(user?.nivel) &&
        [45]?.includes(row?.status_id),
      action: ({ row }) => {
        if (
          user?.configuracoes?.Servico?.localizacao_equipamento_obrigatoria &&
          Boolean(row?.equipamento_id) &&
          !Boolean(row?.localizacao)
        ) {
          return toastWarning('Localização do equipamento não informada');
        }
        changeStatus({
          documento_id: row?.id,
          status_id: row?.vldocto > 0 ? 25 : 26,
        });
      },
    },
    {
      name: 'Imprimir Orçamento',
      icon: 'print',
      menu: true,
      action: ({ row }) =>
        getURLRelatorio({
          data: { codigo: 'OSORC00001', documento_id: row?.id },
          cb: loadGrid,
        }),
    },
    {
      name: 'Imprimir Cupom',
      icon: 'print',
      menu: true,
      action: ({ row }) =>
        getURLRelatorio({
          data: { codigo: 'OSCUPOM001', documento_id: row?.id },
          cb: loadGrid,
        }),
    },
    {
      name: 'Reprovar Orçamento',
      icon: 'thumb_down',
      menu: true,
      show: ({ row }) =>
        !Boolean(user?.permissoes?.block_reprova_orcamento) &&
        [17]?.includes(row?.status_id),
      action: ({ row }) =>
        postStatus({
          data: { documento_id: row?.id, status_id: 19 },
          cb: loadGrid,
        }),
    },
    {
      name: 'Descartar Equipamento',
      icon: 'delete',
      menu: true,
      show: ({ row }) =>
        Boolean(row?.equipamento_id) &&
        !Boolean(user?.permissoes?.block_descarte_equipamento) &&
        [26]?.includes(row?.status_id),
      action: ({ row }) =>
        postStatus({
          data: { documento_id: row?.id, status_id: 27 },
          cb: loadGrid,
        }),
    },
    {
      name: 'Entregar Equipamento',
      icon: 'check',
      menu: true,
      show: ({ row }) =>
        Boolean(row?.equipamento_id) &&
        !Boolean(user?.permissoes?.block_entrega_equipamento) &&
        [26]?.includes(row?.status_id),
      action: ({ row }) =>
        postStatus({
          data: {
            documento_id: row?.id,
            status_id: 6,
            motivo: 'Equipamento Entregue',
          },
          cb: loadGrid,
        }),
    },
    {
      name: 'Registrar Indisponibilidade de Peça',
      icon: 'construction',
      menu: true,
      show: ({ row }) =>
        Boolean(row?.equipamento_id) &&
        !Boolean(user?.permissoes?.block_indisponibilidade_peca) &&
        [45]?.includes(row?.status_id),
      action: ({ row }) =>
        changeStatus({ documento_id: row?.id, status_id: 28 }),
    },
    {
      name: 'Registrar Contato',
      icon: 'contact_phone',
      menu: true,
      action: ({ row }) =>
        openDialog(<ContatoModal item={row} />, 'Registrar Contato'),
    },
  ];

  return (
    <Container fillHeight>
      <Header rotina={rotina} titulo={titulo} colunas={orçamentos?.colunas} />
      <Card fillHeight>
        <DataView
          fillHeight
          grid={orçamentos}
          filterFields={filterFields}
          filterForm={filterForm}
          onFilter={applyFilter}
          columnOverrides={columnOverrides}
          toolbarActions={toolbarActions}
          rowActions={rowActions}
          checkboxSelection
          loading={loading}
          loadGrid={loadGrid}
          getRowHeight={() => 'auto'}
        />
      </Card>
    </Container>
  );
};

export default Orçamentos;
